import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { replayQueuedEvents, optInCapturing, optOutCapturing } from "@/lib/analyticsQueue";
import { setReplayEnabled } from "@/lib/sentryReplay";
import { loadMetaPixel } from "@/lib/metaPixel";
import type { CookiePreferences, CookieConsentContextType } from "@/components/flowly/CookieConsent/types";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  DEFAULT_PREFERENCES,
} from "@/components/flowly/CookieConsent/constants";

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(
  undefined,
);

function readStoredPreferences(): CookiePreferences {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES;
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!stored) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(stored) as CookiePreferences;
    if (typeof parsed.hasConsented === "boolean") return parsed;
  } catch {
    /* invalid JSON — fall through */
  }
  return DEFAULT_PREFERENCES;
}

function persistPreferences(prefs: CookiePreferences) {
  localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(prefs));
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<CookiePreferences>(readStoredPreferences);

  // Sync PostHog, Sentry Session Replay, and Meta Pixel whenever the
  // analytics preference changes. All three stay off until the user
  // explicitly consents — required by GDPR + Israeli Privacy Protection Law.
  //
  // Meta Pixel note: `loadMetaPixel()` is idempotent and a no-op on
  // non-production hostnames. On consent grant it loads fbevents.js and
  // fires PageView immediately, so analytics work without a page reload.
  // It cannot be "opted out" once loaded (Meta's Pixel doesn't expose an
  // opt-out call) — but since we only call it after consent, this is
  // policy-compliant. A user who later toggles analytics off keeps the
  // Pixel in memory for the current session; the next page load will skip
  // loading it because the inline gate in index.html sees the new consent.
  useEffect(() => {
    if (!preferences.hasConsented) return;
    if (preferences.analytics) {
      optInCapturing();
      replayQueuedEvents();
      setReplayEnabled(true);
      loadMetaPixel();
    } else {
      optOutCapturing();
      setReplayEnabled(false);
    }
  }, [preferences.hasConsented, preferences.analytics]);

  const acceptAll = useCallback(() => {
    const prefs: CookiePreferences = {
      hasConsented: true,
      analytics: true,
      consentTimestamp: new Date().toISOString(),
    };
    setPreferences(prefs);
    persistPreferences(prefs);
  }, []);

  const rejectAll = useCallback(() => {
    const prefs: CookiePreferences = {
      hasConsented: true,
      analytics: false,
      consentTimestamp: new Date().toISOString(),
    };
    setPreferences(prefs);
    persistPreferences(prefs);
  }, []);

  const savePreferences = useCallback((analytics: boolean) => {
    const prefs: CookiePreferences = {
      hasConsented: true,
      analytics,
      consentTimestamp: new Date().toISOString(),
    };
    setPreferences(prefs);
    persistPreferences(prefs);
  }, []);

  const resetConsent = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({ preferences, acceptAll, rejectAll, savePreferences, resetConsent }),
    [preferences, acceptAll, rejectAll, savePreferences, resetConsent],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider",
    );
  }
  return context;
}
